/**
 * HW FAQ Split — Filter pills
 * Version: 4.0.0
 *
 * For each [data-hw-faq-filter] element on the page:
 *  1. Finds the nearest .hw-faq-split ancestor.
 *  2. Scans accordion-custom[data-hw-faq-category] anywhere inside the section.
 *  3. Builds filter pills (All + unique categories) and injects them.
 *  4. On pill click: shows/hides accordion rows by category match.
 *
 * Sticky behaviour is owned by the hw-group block (see assets/hw-group-layout.css).
 * No sticky logic lives in this file.
 *
 * Dependencies: none. Accordion expand/collapse is handled natively by
 * the hw-accordion component via <details>/<summary>.
 */

(function () {
  'use strict';

  function readScheme(value) {
    const scheme = (value || '').trim();
    return scheme === '' ? '' : scheme;
  }

  function getNearestColorScheme(el) {
    const scope = el.closest('[class*="color-"]');
    if (!scope) return '';

    const token = Array.from(scope.classList).find(function (cls) {
      return cls.indexOf('color-') === 0 && cls.length > 6;
    });

    return token ? token.replace('color-', '') : '';
  }

  function initFaqFilter(filterEl) {
    // Clear any prior inline hide from an earlier init pass (theme editor may
    // re-render the section with new categories; without this the bar stays
    // hidden if the first pass saw < 2 categories).
    filterEl.style.display = '';

    const root = filterEl.closest('.hw-faq-split');
    if (!root) return;

    // Walk the whole section for accordion rows. Accordions can live anywhere
    // inside the section now that layout is merchant-composed (nested hw-groups).
    const rows = Array.from(
      root.querySelectorAll('accordion-custom[data-hw-faq-category]')
    );

    if (!rows.length) {
      filterEl.style.display = 'none';
      return;
    }

    // Collect unique categories preserving order of appearance.
    // Trim whitespace in JS too so "General " and "General" merge into one pill
    // even if a merchant's input slips through without snippet-side trimming.
    const seen = new Set();
    const categories = [];
    rows.forEach(function (row) {
      const rawCat = row.dataset.hwFaqCategory || '';
      const cat = rawCat.trim();
      // Normalise the data attr on the row so later match logic is comparison-safe.
      if (cat !== rawCat) {
        row.dataset.hwFaqCategory = cat;
      }
      if (cat && !seen.has(cat)) {
        seen.add(cat);
        categories.push(cat);
      }
    });

    // If only one category (or none), no meaningful filter — hide bar
    if (categories.length < 2) {
      filterEl.style.display = 'none';
      return;
    }

    // Reset pills to avoid duplicates during section reloads in the theme editor.
    filterEl.innerHTML = '';

    // Build pill buttons.
    // Each pill gets:
    //   - .hw-faq-split__pill (base layout)
    //   - .color-<scheme> (pill color scheme from filter block)
    //   - type class: `button|button-secondary|button-tertiary` + `button-preset-N`
    //     OR `text-block <preset>` OR none (custom)
    //   - aria-pressed=true on the active pill
    //   - data-hw-faq-active-scheme (used by CSS for active swap)
    const allLabel = filterEl.dataset.allLabel || 'All';
    const showAllTab = filterEl.dataset.showAllTab !== 'false';
    const configuredPillScheme = readScheme(filterEl.dataset.pillColorScheme);
    const configuredActiveScheme = readScheme(filterEl.dataset.pillActiveScheme);
    const inheritedScheme = getNearestColorScheme(filterEl);
    const pillScheme = configuredPillScheme || inheritedScheme || 'scheme-1';
    const activeScheme = configuredActiveScheme || pillScheme;
    const typeClass = (filterEl.dataset.pillTypeClass || '').trim();
    const pillEntries = [];
    if (showAllTab) {
      pillEntries.push({ label: allLabel, category: '' });
    }
    categories.forEach(function (cat) {
      pillEntries.push({ label: cat, category: cat });
    });

    // Tokenise once so we can reason about the exact classes present.
    const typeClassTokens = typeClass ? typeClass.split(/\s+/).filter(Boolean) : [];
    const hasButtonStyleClass = typeClassTokens.some(function (t) {
      return t === 'button' || t === 'button-secondary' || t === 'button-tertiary';
    });
    const hasButtonPreset = typeClassTokens.some(function (t) {
      return t.indexOf('button-preset-') === 0;
    });

    pillEntries.forEach(function (entry, i) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'hw-faq-split__pill';
      // button-preset-N needs at least one button style class (.button / -secondary / -tertiary).
      // Auto-add .button if the merchant picked a preset without a style.
      if (hasButtonPreset && !hasButtonStyleClass) {
        btn.classList.add('button');
      }
      typeClassTokens.forEach(function (cls) {
        btn.classList.add(cls);
      });
      btn.classList.add('color-' + pillScheme);
      btn.textContent = entry.label;
      btn.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');
      btn.dataset.hwFaqPill = entry.category;
      if (activeScheme) btn.dataset.hwFaqActiveScheme = activeScheme;
      filterEl.appendChild(btn);
    });

    // When the All pill is hidden, default to the first category on load.
    if (!showAllTab && categories.length > 0) {
      const firstCategory = categories[0];
      rows.forEach(function (row) {
        if (row.dataset.hwFaqCategory === firstCategory) {
          delete row.dataset.hwFaqHidden;
        } else {
          row.dataset.hwFaqHidden = '';
        }
      });
    }

    // Guard against duplicate listener registration if init runs twice on the
    // same node (dev edge case — Shopify swaps the whole section node on reload,
    // but this keeps us robust).
    if (filterEl.dataset.hwFaqWired === '1') {
      return;
    }
    filterEl.dataset.hwFaqWired = '1';

    // Filter handler
    filterEl.addEventListener('click', function (e) {
      const pill = e.target.closest('.hw-faq-split__pill');
      if (!pill) return;

      const activeCategory = pill.dataset.hwFaqPill; // '' = All

      // Update aria-pressed on pills
      filterEl.querySelectorAll('.hw-faq-split__pill').forEach(function (p) {
        p.setAttribute('aria-pressed', p === pill ? 'true' : 'false');
      });

      // Show/hide rows
      rows.forEach(function (row) {
        if (activeCategory === '' || row.dataset.hwFaqCategory === activeCategory) {
          delete row.dataset.hwFaqHidden;
        } else {
          row.dataset.hwFaqHidden = '';
        }
      });
    });
  }

  function init() {
    document.querySelectorAll('[data-hw-faq-filter]').forEach(initFaqFilter);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor live updates
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-hw-faq-filter]').forEach(initFaqFilter);
  });
})();
